import type { GQL_ID, NestedFilter } from '@@partner:types:graphql'

import React, { useEffect, useState, useCallback } from 'react'
import { CheckPicker, Tag } from 'rsuite'
import { useLazyFetchAssemblers } from '@@partner:graphql/query/employees/allAssemblers.gql'
import { getSearchableEmployee } from '@@partner:helpers/order'
import AppLoader from '@components/common/AppLoader'

export interface AssemblersPickerProps {
  value: GQL_ID[]
  onChange: (value: GQL_ID[]) => void
  placeholder?: string
  size?: 'lg' | 'md' | 'sm' | 'xs'
}

const defaultFilter: NestedFilter = {
  fields: [ 'first_name', 'last_name' ],
  op: 'unwrap'
}

export default function AssemblersPicker ({
  value,
  onChange,
  placeholder = 'Сборщики',
  size = 'sm'
}: AssemblersPickerProps) {
  const [ assemblers, setAssemblers ] = useState<{ label: string; value: GQL_ID; color?: string }[]>([])
  const [ filter, setFilter ] = useState<NestedFilter>(defaultFilter)

  const [ fetchAssemblers, { data, loading, called } ] = useLazyFetchAssemblers({
    pager: { limit: 100 },
    filter,
    sort: { fieldName: 'status', direction: 0 }
  })

  // Загружаем данные при маунте, если есть предзаполненное значение
  useEffect(() => {
    if (value?.length > 0 && !called) {
      fetchAssemblers()
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!loading && data) {
      setAssemblers(getSearchableEmployee(data.assemblers.edges))
    }
  }, [ loading, data ])

  const handleSearch = useCallback((keyword: string) => {
    if (!keyword) {
      setFilter(defaultFilter)
      return
    }
    setFilter({
      or: [
        { fields: [ 'first_name' ], op: 'starts_with', value: [ keyword ] },
        { fields: [ 'last_name' ], op: 'starts_with', value: [ keyword ] }
      ]
    })
  }, [])

  const handleClear = useCallback(() => setFilter(defaultFilter), [])

  return (
    <CheckPicker
      value={value}
      onChange={onChange}
      data={assemblers}
      onOpen={fetchAssemblers}
      searchable
      placeholder={placeholder}
      valueKey="value"
      labelKey="label"
      size={size}
      block
      onSearch={handleSearch}
      onExit={handleClear}
      renderMenu={(menu) => {
        if (loading || !data) {
          return <AppLoader.Inline content="Загружаем" />
        }
        return menu
      }}
      renderMenuItem={(label, item: any) => (
        <Tag key={item?.value} color={item?.color}>
          {label}
        </Tag>
      )}
    />
  )
}
